// Added by Jonghyung Lee on 2020-12-22
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { FC, useState, useEffect, useMemo } from 'react'
import { Prompt } from 'react-router'
import { ConfirmDialog } from '@/components/common/v2/Dialog/ConfirmDialog'

import Intl from '@/helper/Intl'

interface RouteLeavingGuardProps {
  title: string
  when: boolean
  navigate: (location: any) => void
  shouldBlockNavigation: () => boolean // Block해야 하는지 여부를 판단
  content: string
}

const RouteLeavingGuard: FC<RouteLeavingGuardProps> = ({ title, when, navigate, shouldBlockNavigation, content }) => {
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // i18n
  const i18nText = useMemo(
    () => ({
      btnTextConfirm: Intl.getIntlText({ id: 'htmljs_react.global.routeleavingguard.btn_text_leave' }),
      btnTextCancel: Intl.getIntlText({ id: 'htmljs_react.global.routeleavingguard.btn_text_cancel' }),
    }),
    [when]
  )
  
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // states
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [location, setLocation] = useState<string>()
  const [confirmNavigation, setConfirmNavigation] = useState<boolean>(false) // 이동할지 여부를 판단

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // effects
  useEffect(() => {
    if (confirmNavigation) navigate(location)

    return () => {
      setOpenDialog(false)
      setLocation('')
      setConfirmNavigation(false)
    }
    // eslint-disable-next-line
  }, [confirmNavigation])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // handlers
  const showDialog = (location: Location) => {
    setOpenDialog(true)
    setLocation(location.pathname)
  }

  const closeDialog = (cb?: any) => {
    setOpenDialog(false)
    if (typeof cb === 'function') cb()
  }

  const handleBlockNavigation = (location: any) => {
    if (!confirmNavigation && shouldBlockNavigation()) {
      showDialog(location)
      return false
    }
    return true
  }

  const handleConfirmNavigationClick = () => {
    closeDialog(() => {
      if (location) setConfirmNavigation(true)
    })
  }

  return (
    <>
      <Prompt when={when} message={handleBlockNavigation} />
      <ConfirmDialog
        title={title}
        messages={content}
        open={openDialog}
        onConfirmBtnClick={handleConfirmNavigationClick}
        hasCancelBtn
        btnTextConfirm={i18nText.btnTextConfirm}
        btnTextCancel={i18nText.btnTextCancel}
        onCancelBtnClick={closeDialog}
        onCloseBtnClick={closeDialog}
      />
    </>
  )
}

export default RouteLeavingGuard
